import { formatPredictionValue } from '../helpers/prediction-display-helpers';
import { ITeamLineScore } from '../types/game.types';
import { GameID, SeasonWeekID, TeamAbrvID } from '../types/id.types';
import {
  IAccuracyMetrics,
  IAccuracyMetricsByConfidence,
  IAccuracyMetricsByGameType,
  IAccuracyMetricsBySeason,
  IAccuracyMetricsByWeek,
} from '../types/prediction-accuracy.types';

import { GamePredictionRecordsByGameID, GameResultKind } from '../types/prediction.types';

/**
 * Converts a team's quarter-by-quarter line score into a comma-separated string format.
 *
 * This helper is used for storing and analyzing quarter scoring patterns, which are essential
 * for game script reliability calculations (e.g., performance when leading vs trailing).
 *
 * @param {ITeamLineScore} lineScore - Team's line score object containing quarter scores and total points
 *
 * @return {string} Comma-separated string of quarter scores
 * - Format (regular): "Q1,Q2,Q3,Q4,totalPts" (e.g., "7,14,3,10,34")
 * - Format (overtime): "Q1,Q2,Q3,Q4,OT,totalPts" (e.g., "7,14,3,7,3,34")
 *
 * @example
 * const lineScore = { Q1: "7", Q2: "14", Q3: "3", Q4: "10", totalPts: "34", teamID: "16", teamAbv: "KC" };
 * getTeamScoringByQtrString(lineScore);
 * // Returns: "7,14,3,10,34"
 *
 * @example
 * const overtimeScore = { Q1: "7", Q2: "14", Q3: "3", Q4: "7", OT: "3", totalPts: "34", teamID: "16", teamAbv: "KC" };
 * getTeamScoringByQtrString(overtimeScore);
 * // Returns: "7,14,3,7,3,34"
 */
export const getTeamScoringByQtrString = (lineScore: ITeamLineScore): string => {
  const { Q1, Q2, Q3, Q4, OT, totalPts } = lineScore;
  return `${Q1},${Q2},${Q3},${Q4},${OT !== undefined ? `${OT},${totalPts}` : `${totalPts}`}`;
};

/**
 * Determines if a game prediction was correct based on the actual game result
 *
 * Compares the predicted winner against the actual game outcome. Ties are always
 * considered incorrect predictions since no winner was predicted. The function
 * evaluates from the home team's perspective using their game result.
 *
 * @param {TeamAbrvID} predictedWinnerTeamAbrvID - Team abbreviation ID of predicted winner
 * @param {TeamAbrvID} homeTeamAbrvID - Team abbreviation ID of the home team
 * @param {GameResultKind} homeTeamGameResult - Home team's game result ('W', 'L', 'T', or 'N/A')
 *
 * @return {boolean} True if prediction matches actual outcome, false otherwise
 *
 * @example
 * // Predicted home team to win, and they did
 * isCorrectPrediction('16-KC', '16-KC', 'W');
 * // Returns: true
 *
 * @example
 * // Predicted away team to win, home team lost (away team won)
 * isCorrectPrediction('03-BAL', '16-KC', 'L');
 * // Returns: true
 *
 * @example
 * // Game ended in a tie - predictions are always wrong
 * isCorrectPrediction('16-KC', '16-KC', 'T');
 * // Returns: false
 *
 * @example
 * // Game not yet played (N/A result)
 * isCorrectPrediction('16-KC', '16-KC', 'N/A');
 * // Returns: false
 */
export const isCorrectPrediction = (
  predictedWinnerTeamAbrvID: TeamAbrvID,
  homeTeamAbrvID: TeamAbrvID,
  homeTeamGameResult: GameResultKind = 'N/A'
): boolean => {
  if (homeTeamGameResult === 'T') return false;
  const isHomeTeam = predictedWinnerTeamAbrvID === homeTeamAbrvID;
  return isHomeTeam ? homeTeamGameResult === 'W' : homeTeamGameResult === 'L';
};

export const filterPredictionsByModelVersion = (
  predictions: GamePredictionRecordsByGameID,
  modelVersion: string
): GamePredictionRecordsByGameID => {
  const filteredPredictions: GamePredictionRecordsByGameID = {};
  Object.entries(predictions).forEach(([gameID, prediction]) => {
    if (prediction.modelVersion === modelVersion) filteredPredictions[gameID as GameID] = prediction;
  });

  return filteredPredictions;
};

const getPrintByConfidence = (byConfidence: IAccuracyMetricsByConfidence) => {
  const confLow = `${byConfidence.low.correctPredictions}/${byConfidence.low.totalPredictions}`;
  const confLowPct = `${byConfidence.low.accuracyPercentage.toFixed(2)}%`;
  const confMedium = `${byConfidence.medium.correctPredictions}/${byConfidence.medium.totalPredictions}`;
  const confMediumPct = `${byConfidence.medium.accuracyPercentage.toFixed(2)}%`;
  const confHigh = `${byConfidence.high.correctPredictions}/${byConfidence.high.totalPredictions}`;
  const confHighPct = `${byConfidence.high.accuracyPercentage.toFixed(2)}%`;

  return `| **By Confidence**    |                 |                     |
| low                  | ${confLow}      | \`${confLowPct}\`   |
| medium               | ${confMedium}   | \`${confMediumPct}\`|
| high                 | ${confHigh}     | \`${confHighPct}\`  |`;
};

const getPrintByGameType = (byGameType: IAccuracyMetricsByGameType) => {
  const typeDivision = `${byGameType.division.correctPredictions}/${byGameType.division.totalPredictions}`;
  const typeDivisionPct = `${byGameType.division.accuracyPercentage.toFixed(2)}%`;
  const { accuracyPercentage, correctPredictions, totalPredictions } = byGameType.interconference;
  const typeInterconference = `${correctPredictions}/${totalPredictions}`;
  const typeInterconferencePct = `${accuracyPercentage.toFixed(2)}%`;
  const typeConference = `${byGameType.conference.correctPredictions}/${byGameType.conference.totalPredictions}`;
  const typeConferencePct = `${byGameType.conference.accuracyPercentage.toFixed(2)}%`;
  return `| **By Game Type**     |                 |                     |
| division             | ${typeDivision} | \`${typeDivisionPct}\` |
| interconference      | ${typeInterconference} | \`${typeInterconferencePct}\` |
| conference           | ${typeConference} | \`${typeConferencePct}\` |`;
};

const getPrintBySeason = (bySeason: IAccuracyMetricsBySeason, title: string) => {
  const seasonFull = `${bySeason.fullSeason.correctPredictions}/${bySeason.fullSeason.totalPredictions}`;
  const seasonFullPct = `${bySeason.fullSeason.accuracyPercentage.toFixed(2)}%`;
  const seasonStable = `${bySeason.stableSeason.correctPredictions}/${bySeason.stableSeason.totalPredictions}`;
  const seasonStablePct = `${bySeason.stableSeason.accuracyPercentage.toFixed(2)}%`;
  const seasonEarly = `${bySeason.earlySeason.correctPredictions}/${bySeason.earlySeason.totalPredictions}`;
  const seasonEarlyPct = `${bySeason.earlySeason.accuracyPercentage.toFixed(2)}%`;
  return `| **${title} Season**  |                 |                     |
| fullSeason           | ${seasonFull}   | \`${seasonFullPct}\` |
| stableSeason         | ${seasonStable} | \`${seasonStablePct}\` |
| earlySeason          | ${seasonEarly}  | \`${seasonEarlyPct}\` |`;
};

const getPrintByWeek = (bySeasonWeek: IAccuracyMetricsByWeek) => {
  /** TODO: Add byConfidence, byGameType, with a toggle option
   *  When toggle option is ON - display byConfidence and byGameType below the totals for the week
   *  When toggle option is OFF - hide the categories
   */

  const { accuracyPercentage, correctPredictions, seasonWeekID, totalPredictions } = bySeasonWeek;
  const correctAndTotal = `${correctPredictions}/${totalPredictions}`;
  const weeklyPct = `${accuracyPercentage.toFixed(2)}%`;

  return `| **${seasonWeekID}**  | ${correctAndTotal} | \`${weeklyPct}\` |`;
};

export const getPredictionAccuracyReport = (accuracyMetrics: IAccuracyMetrics) => {
  const {
    accuracyPercentage,
    byConfidence,
    byGameType,
    bySeason: bySeasonCollection,
    bySeasonWeek: bySeasonWeekCollection,
    correctPredictions,
    modelVersion,
    totalPredictions,
  } = accuracyMetrics;
  const accuracyTitle = `# Model Version    ${modelVersion}`;
  const correctAndTotal = `${correctPredictions}/${totalPredictions}`;
  const overallPct = `\`${accuracyPercentage.toFixed(2)}%\``;

  let seasonReport = '';
  let weeklyReport = '| **Weekly Accuracy** | | |\n';

  Object.entries(bySeasonCollection).forEach(([title, bySeason], index) => {
    seasonReport += `${index > 0 ? '\n' : ''}` + getPrintBySeason(bySeason, title);
  });

  const seasonWeekIDs = Object.keys(bySeasonWeekCollection);
  seasonWeekIDs.sort();
  seasonWeekIDs.forEach((key) => {
    weeklyReport += getPrintByWeek(bySeasonWeekCollection[key as SeasonWeekID]);
    weeklyReport += '\n';
  });

  const output = `${accuracyTitle}

| Kind                 | Correct / Total | Accuracy Percentage |
| -------------------- | --------------- | ------------------- |
| **Overall Accuracy** | ${correctAndTotal} | ${overallPct} |
${getPrintByConfidence(byConfidence)}
${getPrintByGameType(byGameType)}
${seasonReport}
${weeklyReport}`;

  return output;
};

export const getPredictionAnalysisReport = (allPredictions: GamePredictionRecordsByGameID, modelVersion: string) => {
  // Filter predictions by model version
  const predictions: GamePredictionRecordsByGameID = filterPredictionsByModelVersion(allPredictions, modelVersion);

  let output = `
| Games/Prediction/Actual | Away team  | Home team  | Conf   | Correct    |
| ----------------------- | ---------- | ---------- | ------ | ---------- |
`;

  Object.entries(predictions).forEach(([gameID, prediction], index) => {
    const away = prediction.awayTeam;
    const home = prediction.homeTeam;
    const conf = prediction.confidence === 'low' ? 'LO' : prediction.confidence === 'medium' ? 'MD' : 'HI';
    const correct = prediction.actualResult?.predictionCorrect ? '✅' : '❌';
    const predWinner = prediction.predictedWinnerTeamAbrvID;
    const winner = prediction.actualResult?.winnerTeamAbrvID;
    const predMargin = prediction.predictedMargin;
    const margin = prediction.actualResult?.margin;

    const predAwayScore = `${formatPredictionValue(away.predictedScore)}`.padEnd(4, ' ');
    const predAwayProb = `${away.winProbability.toFixed(2)}%`.padStart(7, ' ');
    const predAway = `${predAwayScore}${predAwayProb}`;

    const predHomeScore = `${formatPredictionValue(home.predictedScore)}`.padEnd(4, ' ');
    const predHomeProb = `${home.winProbability.toFixed(2)}%`.padStart(7, ' ');
    const predHome = `${predHomeScore}${predHomeProb}`;

    const actualAScore = `${prediction.actualResult?.awayScore ?? '--'}`.padEnd(8, ' ');
    const actualHScore = `${prediction.actualResult?.homeScore ?? '--'}`.padEnd(8, ' ');

    const gameNumber = `| **Game ${index + 1}**  |            |            |        |            |`;
    const names = `| **${gameID}** | **${away.teamAbrvID}** | **${home.teamAbrvID}** | **${conf}** | ${correct} |`;
    const predScore = `| Prediction score: | ${predAway} | ${predHome}  | ${predMargin} | **${predWinner}** |`;
    const realScore = `| The actual score: | ${actualAScore} | ${actualHScore} | ${margin}     | **${winner}** |`;

    const msg = `${gameNumber}
${names}
${predScore}
${realScore}
`;
    output += msg;
  });

  return output;
};
